import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, Star, Users, Wifi, UtensilsCrossed, Music, CreditCard, 
  ArrowLeft, Calendar, Play, Image as ImageIcon, Utensils, Wine,
  Dog, Accessibility, PartyPopper, ChevronLeft, ChevronRight,
  ExternalLink, Phone, Mail, Globe, Ticket, Gift, Building2,
  Send, Loader2, Check, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import { SocialShare } from '@/components/SocialShare';
import { ReviewForm } from '@/components/ReviewForm';
import { ReviewsList } from '@/components/ReviewsList';
import BookingWidget from '@/components/BookingWidget';
import api from '@/lib/api';
import { toast } from 'sonner';

const AMENITY_ICONS = {
  'Free WiFi': Wifi,
  'Full Menu': UtensilsCrossed,
  'Live Music': Music,
  'Card Accepted': CreditCard,
  'Craft Focus': Wine,
  'Dog Friendly': Dog,
  'Accessible': Accessibility,
  'Large Groups': Users,
  'Live Events': PartyPopper,
  'Outdoor Seating': Building2,
};

const DEFAULT_HOURS = [
  { day: 'Monday', open_time: '12:00 PM', close_time: '10:00 PM', closed: false },
  { day: 'Tuesday', open_time: '12:00 PM', close_time: '10:00 PM', closed: false },
  { day: 'Wednesday', open_time: '12:00 PM', close_time: '10:00 PM', closed: false },
  { day: 'Thursday', open_time: '12:00 PM', close_time: '11:00 PM', closed: false },
  { day: 'Friday', open_time: '12:00 PM', close_time: '12:00 AM', closed: false },
  { day: 'Saturday', open_time: '11:00 AM', close_time: '12:00 AM', closed: false },
  { day: 'Sunday', open_time: '11:00 AM', close_time: '10:00 PM', closed: false },
];

const DEFAULT_AMENITIES = ['Free WiFi', 'Full Menu', 'Live Music', 'Card Accepted', 'Dog Friendly', 'Accessible'];

const GallerySection = ({ images, videos, venueName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const allMedia = [...(images || []), ...(videos || [])];
  
  if (allMedia.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="aspect-square bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-indigo-400/50" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="relative aspect-video rounded-lg overflow-hidden group">
        <img
          src={allMedia[currentIndex]}
          alt={`${venueName} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover"
        />
        {allMedia.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev + 1) % allMedia.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {allMedia.length > 1 && (
        <div className="grid grid-cols-6 gap-2">
          {allMedia.slice(0, 6).map((media, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-indigo-600 ring-2 ring-indigo-600/20' : 'border-transparent hover:border-indigo-600/50'
              }`}
            >
              <img
                src={media}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// Private Hire Inquiry Form - Industry Standard
const EVENT_TYPES = [
  { value: 'wedding', label: 'Wedding Reception' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'birthday', label: 'Birthday / Celebration' },
  { value: 'cocktail', label: 'Cocktail Party' },
  { value: 'dinner', label: 'Private Dinner' },
  { value: 'meeting', label: 'Meeting / Conference' },
  { value: 'launch', label: 'Product Launch' },
  { value: 'fundraiser', label: 'Fundraiser / Charity' },
  { value: 'other', label: 'Other' },
];

const SEATING_OPTIONS = [
  { value: 'cocktail', label: 'Cocktail / Standing' },
  { value: 'banquet', label: 'Banquet Rounds' },
  { value: 'theater', label: 'Theater Style' },
  { value: 'u-shape', label: 'U-Shape' },
  { value: 'boardroom', label: 'Boardroom' },
  { value: 'mixed', label: 'Mixed / Flexible' },
];

const BUDGET_RANGES = [
  { value: 'under_1000', label: 'Under $1,000' },
  { value: '1000_3000', label: '$1,000 - $3,000' },
  { value: '3000_5000', label: '$3,000 - $5,000' },
  { value: '5000_10000', label: '$5,000 - $10,000' },
  { value: '10000_plus', label: '$10,000+' },
  { value: 'flexible', label: 'Flexible / Discuss' },
];

const PrivateHireForm = ({ venueId, venueName }) => {
  const [form, setForm] = useState({
    venue_id: venueId,
    event_type: '', contact_name: '', contact_email: '', contact_phone: '',
    company_name: '', expected_guests: '', preferred_date: '', preferred_time: '',
    end_time: '', budget: '', seating_arrangement: '', catering_required: false,
    catering_notes: '', bar_service: false, av_requirements: '', entertainment: '',
    special_requests: '', message: '', how_heard: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_email || !form.event_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/inquiries/private-hire', form);
      setSubmitted(true);
      toast.success('Inquiry submitted! We\'ll be in touch within 1-2 business days.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="private-hire-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold mb-2">Enquiry Submitted!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thank you for your interest in hosting at {venueName}. Our events team will review your enquiry 
          and respond within 1-2 business days. A confirmation has been sent to {form.contact_email}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="private-hire-form">
      {/* Section 1: Contact Details */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Contact Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Full Name *</Label>
            <Input placeholder="Your name" value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} required data-testid="ph-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" placeholder="you@example.com" value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} required data-testid="ph-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input type="tel" placeholder="+1 (555) 000-0000" value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} data-testid="ph-phone" />
          </div>
          <div className="space-y-1.5">
            <Label>Company / Organisation</Label>
            <Input placeholder="Company name (if applicable)" value={form.company_name} onChange={(e) => updateField('company_name', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Section 2: Event Details */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Event Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Event Type *</Label>
            <Select value={form.event_type} onValueChange={(v) => updateField('event_type', v)}>
              <SelectTrigger data-testid="ph-event-type"><SelectValue placeholder="Select event type" /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Expected Guests</Label>
            <Input type="number" placeholder="e.g. 50" value={form.expected_guests} onChange={(e) => updateField('expected_guests', e.target.value)} data-testid="ph-guests" />
          </div>
          <div className="space-y-1.5">
            <Label>Preferred Date</Label>
            <Input type="date" value={form.preferred_date} onChange={(e) => updateField('preferred_date', e.target.value)} data-testid="ph-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Start Time</Label>
            <Input type="time" value={form.preferred_time} onChange={(e) => updateField('preferred_time', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Seating Arrangement</Label>
            <Select value={form.seating_arrangement} onValueChange={(v) => updateField('seating_arrangement', v)}>
              <SelectTrigger><SelectValue placeholder="Select layout" /></SelectTrigger>
              <SelectContent>
                {SEATING_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Budget Range</Label>
            <Select value={form.budget} onValueChange={(v) => updateField('budget', v)}>
              <SelectTrigger data-testid="ph-budget"><SelectValue placeholder="Select budget" /></SelectTrigger>
              <SelectContent>
                {BUDGET_RANGES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Section 3: Requirements */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Requirements</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <Label className="cursor-pointer">Catering Required?</Label>
            <input type="checkbox" checked={form.catering_required} onChange={(e) => updateField('catering_required', e.target.checked)} className="w-4 h-4 rounded accent-indigo-600" />
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <Label className="cursor-pointer">Bar / Alcohol Service?</Label>
            <input type="checkbox" checked={form.bar_service} onChange={(e) => updateField('bar_service', e.target.checked)} className="w-4 h-4 rounded accent-indigo-600" />
          </div>
        </div>
        {form.catering_required && (
          <div className="mt-3 space-y-1.5">
            <Label>Catering Notes (dietary needs, menu preferences)</Label>
            <Textarea placeholder="e.g. Vegetarian options needed, nut-free..." value={form.catering_notes} onChange={(e) => updateField('catering_notes', e.target.value)} rows={2} />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="space-y-1.5">
            <Label>AV Requirements</Label>
            <Input placeholder="e.g. Projector, microphone, sound system" value={form.av_requirements} onChange={(e) => updateField('av_requirements', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Entertainment</Label>
            <Input placeholder="e.g. DJ, live band, none" value={form.entertainment} onChange={(e) => updateField('entertainment', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Section 4: Additional Info */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Additional Information</h4>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tell us more about your event</Label>
            <Textarea placeholder="Describe your vision, any special requirements, or questions..." value={form.message} onChange={(e) => updateField('message', e.target.value)} rows={4} data-testid="ph-message" />
          </div>
          <div className="space-y-1.5">
            <Label>How did you hear about us?</Label>
            <Select value={form.how_heard} onValueChange={(v) => updateField('how_heard', v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="social_media">Social Media</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="event">Attended an Event</SelectItem>
                <SelectItem value="search">Search Engine</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" disabled={submitting} data-testid="ph-submit-btn">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit Enquiry</>}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        Our events team typically responds within 1-2 business days. For urgent enquiries, call us directly.
      </p>
    </form>
  );
};



// Private Hire Section — shows packages + inquiry form
const EVENT_TYPE_LABELS = {
  christmas: 'Christmas Party', awards: 'Awards Ceremony', launch: 'Launch Party',
  baby_shower: 'Baby Shower', birthday: 'Birthday Party', wedding: 'Wedding',
  general: 'Private Event', corporate_general: 'Corporate Event',
  conference: 'Conference', holiday: 'Holiday Party', team_building: 'Team Building',
};

const PrivateHireSection = ({ venueId, venueName }) => {
  const [packages, setPackages] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loadingPkgs, setLoadingPkgs] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/private-hire/packages/${venueId}`).catch(() => ({ data: [] })),
      api.get(`/private-hire/spaces/${venueId}`).catch(() => ({ data: [] })),
    ]).then(([pkgRes, spaceRes]) => {
      setPackages(pkgRes.data || []);
      setSpaces(spaceRes.data || []);
    }).finally(() => setLoadingPkgs(false));
  }, [venueId]);

  if (loadingPkgs) return <div className="text-center py-10 text-muted-foreground">Loading packages...</div>;

  const corporate = packages.filter(p => p.category === 'corporate');
  const social = packages.filter(p => p.category === 'social');
  const hasPackages = packages.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-600" /> Private Hire & Events</CardTitle>
          <p className="text-sm text-muted-foreground">
            Host your next event at {venueName}. {hasPackages ? 'Browse our packages or request a custom quote.' : 'Submit an enquiry and our events team will respond within 1-2 business days.'}
          </p>
        </CardHeader>
      </Card>

      {/* Event Spaces */}
      {spaces.length > 0 && (
        <div>
          <h3 className="font-bold text-lg mb-3">Our Spaces</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {spaces.map(space => (
              <Card key={space.id} className="overflow-hidden">
                {space.images?.[0] && <div className="aspect-video"><img src={space.images[0]} alt={space.name} className="w-full h-full object-cover" /></div>}
                <CardContent className="p-4">
                  <h4 className="font-bold">{space.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{space.description}</p>
                  <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                    <span>{space.capacity_min}–{space.capacity_max} guests</span>
                    {space.size_sqft && <span>{space.size_sqft} sq ft</span>}
                  </div>
                  {space.features?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{space.features.map(f => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}</div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Corporate Packages */}
      {corporate.length > 0 && (
        <div>
          <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><Badge className="bg-blue-100 text-blue-700">Corporate</Badge> Packages</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {corporate.map(pkg => (
              <Link key={pkg.id} to={`/private-hire/${pkg.id}`}>
                <Card className="hover:border-indigo-500 transition-colors h-full">
                  {pkg.images?.[0] && <div className="aspect-video"><img src={pkg.images[0]} alt={pkg.name} className="w-full h-full object-cover" /></div>}
                  <CardContent className="p-4">
                    <Badge variant="outline" className="text-xs mb-2">{EVENT_TYPE_LABELS[pkg.event_type] || pkg.event_type}</Badge>
                    <h4 className="font-bold text-lg">{pkg.name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{pkg.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-indigo-600 font-bold">From ${pkg.base_price?.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">{pkg.duration_label || `${pkg.duration_hours}hr`} · {pkg.min_guests}–{pkg.max_guests} guests</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Social Packages */}
      {social.length > 0 && (
        <div>
          <h3 className="font-bold text-lg mb-3 flex items-center gap-2"><Badge className="bg-pink-100 text-pink-700">Social</Badge> Packages</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {social.map(pkg => (
              <Link key={pkg.id} to={`/private-hire/${pkg.id}`}>
                <Card className="hover:border-pink-500 transition-colors h-full">
                  {pkg.images?.[0] && <div className="aspect-video"><img src={pkg.images[0]} alt={pkg.name} className="w-full h-full object-cover" /></div>}
                  <CardContent className="p-4">
                    <Badge variant="outline" className="text-xs mb-2">{EVENT_TYPE_LABELS[pkg.event_type] || pkg.event_type}</Badge>
                    <h4 className="font-bold text-lg">{pkg.name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{pkg.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-pink-600 font-bold">From ${pkg.base_price?.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">{pkg.duration_label || `${pkg.duration_hours}hr`} · {pkg.min_guests}–{pkg.max_guests} guests</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Custom Inquiry */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold">Need something custom?</h4>
              <p className="text-sm text-muted-foreground">Send us your requirements and we'll create a tailored proposal.</p>
            </div>
            <Button onClick={() => setShowInquiryForm(!showInquiryForm)} variant={showInquiryForm ? 'secondary' : 'default'}>
              {showInquiryForm ? 'Hide Form' : 'Custom Inquiry'}
            </Button>
          </div>
          {showInquiryForm && <div className="mt-6 border-t pt-6"><PrivateHireForm venueId={venueId} venueName={venueName} /></div>}
        </CardContent>
      </Card>
    </div>
  );
};


const VenueDetail = () => {
  const { id, idOrState, state, city, neighborhood, slug } = useParams();
  const effectiveId = id || (idOrState && /^[0-9a-f]{8}-/i.test(idOrState) ? idOrState : null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [venue, setVenue] = useState(null);
  const [events, setEvents] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimForm, setClaimForm] = useState({ name: '', email: '', phone: '', role: 'owner', message: '' });
  const [claimSubmitting, setClaimSubmitting] = useState(false);

  const venueId = effectiveId;
  const isSlugRoute = !!slug;

  useEffect(() => {
    fetchVenue();
  }, [effectiveId, slug]);

  const fetchVenue = async () => {
    try {
      let venueResponse;
      if (isSlugRoute) {
        venueResponse = await api.get(`/venues/by-slug/${state}/${city}/${neighborhood}/${slug}`);
      } else {
        venueResponse = await api.get(`/venues/${venueId}`);
      }
      setVenue(venueResponse.data);
      const vid = venueResponse.data.id;
      
      const eventsResponse = await api.get('/events');
      setEvents(eventsResponse.data.filter(e => e.venue_id === vid));
      
      const vouchersResponse = await api.get('/vouchers');
      setVouchers(vouchersResponse.data.filter(v => v.venue_id === vid));
      
      const reviewsResponse = await api.get('/reviews', {
        params: { target_type: 'venue', target_id: vid }
      });
      setReviews(reviewsResponse.data);
    } catch (error) {
      toast.error('Failed to load venue');
    } finally {
      setLoading(false);
    }
  };

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      </ConsumerLayout>
    );
  }

  if (!venue) {
    return (
      <ConsumerLayout hideFooter>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Venue not found</h1>
          <Button onClick={() => navigate('/venues')} data-testid="back-to-venues-btn">
            Browse Venues
          </Button>
        </div>
      </ConsumerLayout>
    );
  }

  const amenities = venue.amenities?.length > 0 ? venue.amenities : DEFAULT_AMENITIES;
  const hours = venue.hours?.length > 0 ? venue.hours : DEFAULT_HOURS;
  const menus = venue.menus?.length > 0 ? venue.menus : [
    { name: 'Drink Menu', url: '#' },
    { name: 'Food Menu', url: '#' }
  ];

  return (
    <ConsumerLayout>

      {/* Hero Section */}
      <div className="relative h-[450px] overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
          {venue.images && venue.images[0] ? (
            <img
              src={venue.images[0]}
              alt={venue.name}
              className="w-full h-full object-cover opacity-40"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-900 to-purple-900" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <Button
              variant="ghost"
              className="mb-4 text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge className="bg-indigo-600 text-white border-0">
                PARTNER VENUE
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-0">
                {venue.price_level || '$$'} {venue.venue_type || 'Bar & Restaurant'}
              </Badge>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="venue-name">
              {venue.name}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-white/90 mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                <span>{venue.address}</span>
              </div>
              {avgRating && (
                <div className="flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-bold">{avgRating}</span>
                  <span className="text-white/70">({reviews.length} reviews)</span>
                </div>
              )}
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {venue.images?.length > 0 && (
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="bg-white/10 hover:bg-white/20 text-white border-0"
                  onClick={() => setActiveTab('gallery')}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Photos
                </Button>
              )}
              {venue.videos?.length > 0 && (
                <Button variant="secondary" size="sm" className="bg-white/10 hover:bg-white/20 text-white border-0">
                  <Play className="w-4 h-4 mr-2" />
                  Video
                </Button>
              )}
              <Button 
                variant="secondary" 
                size="sm" 
                className="bg-white/10 hover:bg-white/20 text-white border-0"
                onClick={() => setActiveTab('overview')}
              >
                <Utensils className="w-4 h-4 mr-2" />
                Menu
              </Button>
              <SocialShare title={venue.name} description={venue.description} className="bg-white/10 hover:bg-white/20 text-white border-0" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-14 bg-transparent border-0 space-x-6">
              <TabsTrigger 
                value="overview" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-overview"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="gallery" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-gallery"
              >
                Gallery
              </TabsTrigger>
              <TabsTrigger 
                value="events" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-events"
              >
                Events & Tickets
              </TabsTrigger>
              <TabsTrigger 
                value="experiences" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-experiences"
              >
                Experiences
              </TabsTrigger>
              <TabsTrigger 
                value="location" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-location"
              >
                Location & Hours
              </TabsTrigger>
              <TabsTrigger 
                value="reviews" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-reviews"
              >
                Reviews
              </TabsTrigger>
              <TabsTrigger 
                value="private-hire" 
                className="text-base data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 data-[state=active]:text-indigo-600 rounded-none bg-transparent"
                data-testid="tab-private-hire"
              >
                Private Hire
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-8 mt-0">
                {/* Photo Gallery Preview */}
                {venue.images?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 rounded-xl overflow-hidden" data-testid="venue-overview-gallery">
                    {venue.images.slice(0, 6).map((img, i) => (
                      <div key={i} className={`${i === 0 ? 'col-span-2 row-span-2' : ''} aspect-video overflow-hidden`}>
                        <img src={img} alt={`${venue.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Welcome Section */}
                <Card data-testid="venue-welcome">
                  <CardHeader>
                    <CardTitle className="text-2xl">Welcome to {venue.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      {venue.description}
                    </p>
                    {venue.tagline && (
                      <p className="text-muted-foreground italic">
                        {venue.tagline}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* What to Expect */}
                <Card data-testid="venue-amenities">
                  <CardHeader>
                    <CardTitle>What to Expect</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {amenities.map((amenity, index) => {
                        const Icon = AMENITY_ICONS[amenity] || Building2;
                        const amenitySlug = amenity.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        return (
                          <Link
                            key={index}
                            to={`/venues/amenity/${encodeURIComponent(amenity)}`}
                            className="flex items-center gap-3 p-3 border rounded-lg hover:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-indigo-600" />
                            </div>
                            <span className="text-sm font-medium group-hover:text-indigo-600 transition-colors">{amenity}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* On The Menu */}
                <Card data-testid="venue-menus">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      On The Menu
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-3">
                      {menus.map((menu, index) => (
                        <Button
                          key={index}
                          variant="outline"
                          className="gap-2"
                          onClick={() => {
                            if (menu.url && menu.url !== '#') {
                              window.open(menu.url, '_blank');
                            } else {
                              toast.info('Menu not yet uploaded by venue');
                            }
                          }}
                        >
                          {menu.name.toLowerCase().includes('drink') ? (
                            <Wine className="w-4 h-4" />
                          ) : (
                            <Utensils className="w-4 h-4" />
                          )}
                          {menu.name}
                          {menu.url && menu.url !== '#' && <ExternalLink className="w-3 h-3 opacity-50" />}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">Menus open as PDF or image in a new tab</p>
                  </CardContent>
                </Card>

                {/* Upcoming Events Preview */}
                {events.length > 0 && (
                  <Card data-testid="venue-events-preview">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Upcoming Events</CardTitle>
                      {events.length > 3 && (
                        <Button variant="link" onClick={() => setActiveTab('events')}>
                          View all
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {events.slice(0, 3).map((event) => (
                          <Link
                            key={event.id}
                            to={`/events/${event.id}`}
                            className="flex items-center gap-4 p-4 border rounded-lg hover:border-indigo-600/50 transition-colors group"
                            data-testid={`event-preview-${event.id}`}
                          >
                            <div className="w-16 h-16 rounded-lg bg-indigo-600 text-white flex flex-col items-center justify-center text-center">
                              <span className="text-2xl font-bold">{event.date?.split('-')[2] || '??'}</span>
                              <span className="text-xs uppercase">{event.date ? new Date(event.date).toLocaleString('en', { month: 'short' }) : 'TBD'}</span>
                            </div>
                            <div className="flex-grow">
                              <h4 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                                {event.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">{event.time}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">From</p>
                              <p className="text-lg font-bold">${event.price}</p>
                            </div>
                            {event.gold_exclusive && (
                              <Badge className="bg-[#D4AF37] text-black">GOLD</Badge>
                            )}
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Exclusive Experiences Preview */}
                {vouchers.length > 0 && (
                  <Card data-testid="venue-experiences-preview">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Exclusive Experiences</CardTitle>
                      {vouchers.length > 2 && (
                        <Button variant="link" onClick={() => setActiveTab('experiences')}>
                          View all
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vouchers.slice(0, 2).map((voucher) => (
                          <Link
                            key={voucher.id}
                            to={`/experiences/${voucher.id}`}
                            className="block border rounded-lg overflow-hidden hover:border-indigo-600/50 transition-colors group"
                            data-testid={`experience-preview-${voucher.id}`}
                          >
                            <div className="aspect-[16/9] bg-gradient-to-br from-indigo-600 to-purple-600 relative">
                              {voucher.images?.[0] ? (
                                <img src={voucher.images[0]} alt={voucher.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Gift className="w-12 h-12 text-white/50" />
                                </div>
                              )}
                              <Badge className="absolute top-3 left-3 bg-indigo-600 text-white">
                                VOUCHER
                              </Badge>
                            </div>
                            <div className="p-4">
                              <h4 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors mb-1">
                                {voucher.title}
                              </h4>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {voucher.description}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className="text-xl font-bold">${voucher.price}</span>
                                <Button size="sm">Buy Now</Button>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Community Vibes (Reviews Preview) */}
                {reviews.length > 0 && (
                  <Card data-testid="venue-reviews-preview">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Community Vibes</CardTitle>
                      <Button variant="link" onClick={() => setActiveTab('reviews')}>
                        View all
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {reviews.slice(0, 2).map((review) => (
                          <div key={review.id} className="border-b pb-4 last:border-0">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium">{review.user_name}</span>
                              <span className="text-sm text-muted-foreground">•</span>
                              <span className="text-sm text-muted-foreground">
                                {new Date(review.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-muted-foreground line-clamp-2">{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Gallery Tab */}
              <TabsContent value="gallery" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Photo Gallery</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GallerySection 
                      images={venue.images} 
                      videos={venue.videos}
                      venueName={venue.name} 
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Events Tab */}
              <TabsContent value="events" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Events & Tickets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {events.length > 0 ? (
                      <div className="space-y-4">
                        {events.map((event) => (
                          <Link
                            key={event.id}
                            to={`/events/${event.id}`}
                            className="flex items-center gap-4 p-4 border rounded-lg hover:border-indigo-600/50 transition-colors group"
                          >
                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 flex-shrink-0">
                              {event.images?.[0] ? (
                                <img src={event.images[0]} alt={event.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Ticket className="w-8 h-8 text-indigo-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-grow">
                              <h4 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                                {event.title}
                              </h4>
                              <p className="text-sm text-muted-foreground">{event.date} • {event.time}</p>
                              <div className="flex gap-2 mt-2">
                                {event.category && (
                                  <Badge variant="secondary">{event.category.replace('_', ' ')}</Badge>
                                )}
                                {event.gold_exclusive && (
                                  <Badge className="bg-[#D4AF37] text-black">GOLD</Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold">${event.price}</p>
                              <Button size="sm" className="mt-2">Get Tickets</Button>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No upcoming events at this venue</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Experiences Tab */}
              <TabsContent value="experiences" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Experiences & Vouchers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vouchers.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vouchers.map((voucher) => (
                          <Link
                            key={voucher.id}
                            to={`/experiences/${voucher.id}`}
                            className="block border rounded-lg overflow-hidden hover:border-purple-600/50 transition-colors group"
                            data-testid={`voucher-card-${voucher.id}`}
                          >
                            <div className="aspect-[16/9] bg-gradient-to-br from-purple-600 to-pink-600 relative">
                              {voucher.images?.[0] ? (
                                <img src={voucher.images[0]} alt={voucher.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Gift className="w-12 h-12 text-white/50" />
                                </div>
                              )}
                              <Badge className="absolute top-3 left-3 bg-purple-600 text-white">EXPERIENCE</Badge>
                              {voucher.gold_price && (
                                <Badge className="absolute top-3 right-3 bg-[#D4AF37] text-black">
                                  GOLD: ${voucher.gold_price}
                                </Badge>
                              )}
                            </div>
                            <div className="p-4">
                              <h4 className="font-bold text-foreground group-hover:text-purple-600 transition-colors mb-1">
                                {voucher.title}
                              </h4>
                              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{voucher.description}</p>
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-xl font-bold">${voucher.price}</span>
                                  <p className="text-xs text-muted-foreground">Valid until {voucher.valid_until}</p>
                                </div>
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700">View</Button>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No experiences available at this venue</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Location Tab */}
              <TabsContent value="location" className="mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Location & Getting Here</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Embedded Google Map */}
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <iframe
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g&q=${encodeURIComponent(`${venue.name}, ${venue.address}`)}`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Venue Location"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-lg font-medium">{venue.address}</p>
                      <p className="text-muted-foreground">{venue.district} District, Denver</p>
                    </div>
                    <Button
                      className="w-full"
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address + ', Denver, CO')}`, '_blank')}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Get Directions
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Reviews Tab */}
              <TabsContent value="reviews" className="space-y-6 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Reviews ({reviews.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReviewsList reviews={reviews} />
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Write a Review</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReviewForm
                      targetType="venue"
                      targetId={id}
                      onSuccess={fetchVenue}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Private Hire Tab */}
              <TabsContent value="private-hire" className="space-y-6 mt-0">
                <PrivateHireSection venueId={venue.id} venueName={venue.name} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Booking Widget */}
            <div className="sticky top-20">
              <BookingWidget 
                venue={venue} 
                events={events} 
                vouchers={vouchers}
              />

              {/* Opening Hours */}
              <Card className="mt-6" data-testid="venue-hours">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Clock className="w-5 h-5" />
                    Opening Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {hours.map((h, index) => {
                      const to12 = (t) => {
                        if (!t) return '';
                        const [hh, mm] = t.split(':').map(Number);
                        if (isNaN(hh)) return t;
                        const period = hh >= 12 ? 'PM' : 'AM';
                        const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
                        return `${h12}:${String(mm || 0).padStart(2, '0')} ${period}`;
                      };
                      return (
                        <div 
                          key={index} 
                          className="flex justify-between text-sm py-1 border-b last:border-0"
                        >
                          <span className="font-medium">{h.day}</span>
                          <span className="text-muted-foreground">
                            {h.closed ? 'Closed' : `${to12(h.open_time)} - ${to12(h.close_time)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Claim This Venue — Bottom Banner for unclaimed venues */}
      {venue?.claimed === false && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <Card className="border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Building2 className="w-8 h-8 text-amber-500 shrink-0" />
                <div>
                  <h3 className="font-bold">Own or manage {venue.name}?</h3>
                  <p className="text-sm text-muted-foreground">Claim your free profile to add events, experiences, and reach thousands of eventgoers.</p>
                </div>
              </div>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white shrink-0" onClick={() => setShowClaimDialog(true)}>Claim This Venue</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}

      {/* Claim Venue Dialog */}
      {venue && <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Claim {venue.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Verify you're the owner or manager. We'll review within 48 hours.</p>
            <div><Label>Your Name *</Label><Input value={claimForm.name} onChange={e => setClaimForm({...claimForm, name: e.target.value})} placeholder="Full name" /></div>
            <div><Label>Business Email *</Label><Input type="email" value={claimForm.email} onChange={e => setClaimForm({...claimForm, email: e.target.value})} placeholder="you@venue.com" /></div>
            <div><Label>Phone</Label><Input value={claimForm.phone} onChange={e => setClaimForm({...claimForm, phone: e.target.value})} /></div>
            <div><Label>Your Role</Label><Select value={claimForm.role} onValueChange={v => setClaimForm({...claimForm, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="owner">Owner</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="marketing">Marketing</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowClaimDialog(false)}>Cancel</Button><Button className="bg-amber-500 hover:bg-amber-600" disabled={claimSubmitting || !claimForm.name || !claimForm.email} onClick={async () => { setClaimSubmitting(true); try { await api.post(`/admin/venues/${venue.id}/claim`, claimForm); toast.success('Claim submitted!'); setShowClaimDialog(false); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setClaimSubmitting(false); } }}>{claimSubmitting ? 'Submitting...' : 'Submit Claim'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>}
    </ConsumerLayout>
  );
};

export default VenueDetail;
